import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Row} from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const UiCompTypeAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [uiCompTypeAttrs, setUiCompTypeAttrs] = useState({
    id: '',
    name: '',
    description: '',
    dataSrcType: '',
    dataSrcStyle: '',
    dataLenStyle: '',
    attrList: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: ''
  });

  const [currentAttrs, setCurrentAttrs] = useState(false);


  // Ui Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setUiCompTypeAttrs(props.objDetails);
      setCurrentAttrs(props.objDetails);
    }
  }, [props]);

  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg, setErrMsg] = useState('');


  const dataSrcTypeOptions = [
    { key: "DATAPOINT", value: "DATAPOINT" },
    { key: "ALARM",     value: "ALARM" }
  ];

  const dataSrcStyleOptions = [
    { key: "FIXED",    value: "FIXED" },
    { key: "ADAPTIVE", value: "ADAPTIVE" }
  ];

  const dataLenStyleOptions = [
    { key: "POINT",  value: "POINT" },
    { key: "SERIES", value: "SERIES" }
  ];


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setUiCompTypeAttrs({ ...uiCompTypeAttrs, [e.target.name]: e.target.value })
  }

  const onDataSrcTypeSelect = (key, event) => {
    setUiCompTypeAttrs({ ...uiCompTypeAttrs, dataSrcType: key })
  };

  const onDataSrcStyleSelect = (key, event) => {
    setUiCompTypeAttrs({ ...uiCompTypeAttrs, dataSrcStyle: key })
  };

  const onDataLengthSelect = (key, event) => {
    setUiCompTypeAttrs({ ...uiCompTypeAttrs, dataLenStyle: key })
  };


  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setUiCompTypeAttrs(currentAttrs);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

   if (!uiCompTypeAttrs.name || uiCompTypeAttrs.name.length === 0) {
      setErrMsg(t("I18N_ERR_Need_uiCompType_name"));
    } else if (uiCompTypeAttrs.name.length > 100) {
      setErrMsg(t("I18N_ERR_Max_len_name") + "100");
    } else if (uiCompTypeAttrs.name !== currentAttrs.name) {
      validateName();
    } else {
      validateOtherFields();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateName() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("ui-comp-types?name=" + uiCompTypeAttrs.name);

        resolve(res.data);

        if (res.data.length > 0) {
          setErrMsg(t("I18N_ERR_Dup_name"));
        } else {
          validateOtherFields();
        }

        return;

      } catch (error) {

        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }

        return;
      }
    });
  }

  function validateOtherFields() {
    // Validate form inputs
    if (!uiCompTypeAttrs.description || uiCompTypeAttrs.description.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
    } else if (uiCompTypeAttrs.description.length > 250) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + "250");

    } else if (!uiCompTypeAttrs.attrList || uiCompTypeAttrs.attrList.trim().length === 0) {
      setErrMsg(t("I18N_ERR_Need_attr_list"));
    } else if (uiCompTypeAttrs.attrList.length > 1000) {
      setErrMsg(t("I18N_ERR_Max_len_attr_list") + "1000");

    } else if (uiCompTypeAttrs.dataSrcType === '') {
      setErrMsg(t("I18N_ERR_Need_dataSrcType"));

    } else if (uiCompTypeAttrs.dataSrcStyle === '') {
      setErrMsg(t("I18N_ERR_Need_dataSrcStyle"));

    } else if (uiCompTypeAttrs.dataLenStyle === '') {
      setErrMsg(t("I18N_ERR_Need_dataLenStyle"));

    } else {
      setErrMsg('');
      updateUiCompType();
    }
  }

  const updateUiCompType = () => {

    const reqBody = uiCompTypeAttrs;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.put("ui-comp-types/" + uiCompTypeAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setCurrentAttrs(uiCompTypeAttrs);

          // Restore Ui to viewing mode
          setEditMode(false);
          setErrMsg('');
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_UiCompType_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_UiCompType_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Name")}: *</Col>
                  <Col>{t("I18N_Description")}: *</Col>
                  <Col md={{ span: 6}}>{t("I18N_Attr_list")}:</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>{uiCompTypeAttrs.name}</Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="description" value={uiCompTypeAttrs.description}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={{ span: 6}}>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="attrList" value={uiCompTypeAttrs.attrList}
                              onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Data_src_type")}:</Col>
                  <Col>{t("I18N_Data_src_style")}:</Col>
                  <Col>{t("I18N_Data_len_style")}:</Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("I18N_" + uiCompTypeAttrs.dataSrcType)}
                      onSelect={onDataSrcTypeSelect}>
                      {dataSrcTypeOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("I18N_" + uiCompTypeAttrs.dataSrcStyle)}
                      onSelect={onDataSrcStyleSelect}>
                      {dataSrcStyleOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={t("I18N_" + uiCompTypeAttrs.dataLenStyle)}
                      onSelect={onDataLengthSelect}>
                      {dataLenStyleOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Name")}:</Col>
                <Col>{t("I18N_Description")}:</Col>
                <Col sm={{span: 6}}>{t("I18N_Attr_list")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{uiCompTypeAttrs.name}</Col>
                <Col>{uiCompTypeAttrs.description}</Col>
                <Col sm={{span: 6}}>{uiCompTypeAttrs.attrList}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Data_src_type")}:</Col>
                <Col>{t("I18N_Data_src_style")}:</Col>
                <Col>{t("I18N_Data_len_style")}:</Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{t("I18N_" + uiCompTypeAttrs.dataSrcType)}</Col>
                <Col>{t("I18N_" + uiCompTypeAttrs.dataSrcStyle)}</Col>
                <Col>{t("I18N_" + uiCompTypeAttrs.dataLenStyle)}</Col>
                <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{uiCompTypeAttrs.creatorUsername}</Col>
                <Col>{formatDate(uiCompTypeAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{uiCompTypeAttrs.updaterUsername}</Col>
                <Col>{formatDate(uiCompTypeAttrs.timeUpdated)}</Col>
              </Row>
            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </div>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateUiCompTypeErrMsg}
              </span>
*/}
          </Form.Group>
        </div>

      </Card>
    </>
  );
}
 
export default UiCompTypeAdminDetailsView;
